import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '../../common/Button';
import FullBlockingModal from '../../common/FullBlockingModal';
import { Profile } from '../../../types/profile';
import { useAdminStore } from '../../../stores/adminStore';
import { toast } from 'react-toastify';

interface DeleteUserModalProps {
  profile: Profile;
  onClose: () => void;
  onSuccess?: () => void;
}

const DeleteUserModal: React.FC<DeleteUserModalProps> = ({ profile, onClose, onSuccess }) => {
  const { t } = useTranslation(['admin', 'common']);
  const [confirmationInput, setConfirmationInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const { anonymizeUser } = useAdminStore();

  const confirmationWord = t('deleteUser.confirmWord', { ns: 'admin', defaultValue: 'SUPPRIMER' });
  const username = `${profile.first_name} ${profile.last_name ?? ''}`.trim();
  const canConfirm = confirmationInput.trim().toUpperCase() === confirmationWord.toUpperCase();

  const handleConfirm = async () => {
    if (!canConfirm || deleting) return;
    setDeleting(true);
    try {
      await anonymizeUser(profile.id);
      toast.success(
        t('deleteUser.success', { ns: 'admin', defaultValue: 'User deleted successfully' })
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(
        (err as Error).message ||
          t('deleteUser.error', { ns: 'admin', defaultValue: 'Could not delete this user.' })
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <FullBlockingModal innerClassName="md:max-w-[32rem]">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-red-600">
          {t('deleteUser.title', { ns: 'admin', defaultValue: 'Delete user' })}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          type="button"
        >
          <X size={22} />
        </button>
      </div>

      <p className="text-sm text-gray-700 mb-2">
        {t('deleteUser.warning', {
          ns: 'admin',
          username,
          defaultValue:
            'You are about to permanently delete {{username}}. Their personal information will be erased and they will no longer be able to log in. This action cannot be undone.',
        })}
      </p>

      <div className="flex flex-col gap-2 mt-4">
        <label htmlFor="delete-confirmation" className="text-sm font-semibold text-gray-700">
          {t('deleteUser.confirmPrompt', {
            ns: 'admin',
            word: confirmationWord,
            defaultValue: 'Type {{word}} to confirm',
          })}
        </label>
        <input
          id="delete-confirmation"
          type="text"
          value={confirmationInput}
          onChange={e => setConfirmationInput(e.target.value)}
          className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
          autoComplete="off"
        />
      </div>

      <div className="flex flex-row justify-end gap-4 mt-8">
        <Button
          variant="simple-text"
          onClick={onClose}
          className="font-bold rounded-full px-8 py-2 w-full md:w-auto"
          type="button"
        >
          {t('common:form_actions.cancel')}
        </Button>

        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={!canConfirm || deleting}
          className="font-bold rounded-full px-8 py-2 w-full md:w-auto !bg-red-600 hover:!bg-red-700 disabled:!bg-red-300"
          type="button"
        >
          {deleting
            ? t('loading', { ns: 'common', defaultValue: 'Loading...' })
            : t('deleteUser.confirmButton', { ns: 'admin', defaultValue: 'Delete user' })}
        </Button>
      </div>
    </FullBlockingModal>
  );
};

export default DeleteUserModal;
